/** @jsx jsx */
import { jsx, Box, ThemeUIStyleObject, ResponsiveStyleValue } from "theme-ui"
import React from "react"

import { PrimitiveProps } from "@styles/types"
import spacing from "@styles/design-tokens/03.spacing/spacing"
import { TAXONOMIES_MAP } from "../../../carlsberg-headless/taxonomies-map"

const gridColumns = 12

const ColToWidth = (
  n: ResponsiveStyleValue<string | number>
):
  | ResponsiveStyleValue<string | number>
  | ResponsiveStyleValue<string | number>[]
  | false => {
  if (Array.isArray(n)) {
    return n.map(w => ColToWidth(w as string | number)) as ResponsiveStyleValue<
      string | number
    >[]
  }
  return !!n && (typeof n === "number" ? `${(n / gridColumns) * 100}%` : n)
}

const AlignToCssProp = (
  n: ResponsiveStyleValue<string>
): ResponsiveStyleValue<string> | ResponsiveStyleValue<string>[] | false => {
  if (Array.isArray(n)) {
    return n.map(w =>
      AlignToCssProp(w as string)
    ) as ResponsiveStyleValue<string>[]
  }
  return (
    !!n &&
    (n === ""
      ? ""
      : n === "start"
      ? "flex-start"
      : n === "end"
      ? "flex-end"
      : "center")
  )
}

const spacingToCssProp = (
  n: ResponsiveStyleValue<number>,
  negative: boolean
): ResponsiveStyleValue<number> | ResponsiveStyleValue<number>[] | false => {
  if (Array.isArray(n)) {
    return n.map(w =>
      spacingToCssProp(w as number, negative as boolean)
    ) as ResponsiveStyleValue<number>[]
  }

  return !!n && !!spacing[n] && (negative ? `-${spacing[n]}` : spacing[n])
}

export interface FlexRowGrid
  extends PrimitiveProps,
    React.ComponentPropsWithoutRef<"div"> {}

export interface FlexRowProps
  extends PrimitiveProps,
    React.ComponentPropsWithoutRef<"div"> {
  align?: string
  vAlign?: string
  spacing?: number[]
  sx?: ThemeUIStyleObject
}

export const FlexRowStyles: ThemeUIStyleObject = {
  display: "flex",
  flexWrap: "wrap",
}

const FlexRow: React.FC<FlexRowProps> = ({
  align,
  vAlign,
  spacing,
  children,
  sx,
  ...props
}) => {
  const alignment = (
    !!align ? AlignToCssProp(align) : null
  ) as ResponsiveStyleValue<string>
  const vAlignment = (
    !!vAlign ? AlignToCssProp(vAlign) : null
  ) as ResponsiveStyleValue<string>

  const spacingCellPadding = (
    !!spacing ? spacingToCssProp(spacing, false) : null
  ) as ResponsiveStyleValue<string>[]

  const spacingRowMargin = (
    !!spacing ? spacingToCssProp(spacing, true) : null
  ) as ResponsiveStyleValue<string>

  const mappedChildren = React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        sx: {
          ...(spacingCellPadding && {
            paddingLeft: spacingCellPadding,
            paddingRight: spacingCellPadding,
            paddingTop: spacingCellPadding,
            paddingBottom: spacingCellPadding,
          }),
        },
        ...child.props,
      })
    }
    return child
  })

  return (
    <Box
      sx={{
        ...FlexRowStyles,
        ...(alignment && { justifyContent: alignment }),
        ...(vAlignment && { alignItems: vAlignment }),
        ...(spacingRowMargin && {
          marginLeft: spacingRowMargin,
          marginRight: spacingRowMargin,
          marginTop: spacingRowMargin,
          marginBottom: spacingRowMargin,
        }),
        ...sx,
      }}
      {...props}
    >
      {mappedChildren}
    </Box>
  )
}

export interface FlexCellProps
  extends PrimitiveProps,
    React.ComponentPropsWithoutRef<"div"> {
  col?: string | number | string[] | number[]
  offset?: string[] | number[]
  sx?: ThemeUIStyleObject
  taxonomies?: string[]
}

const getTaxonomiesColumnSize = (taxonomies: string[]) => {
  if (!taxonomies) return

  switch (true) {
    case taxonomies.includes(TAXONOMIES_MAP.container_module.grid_6_columns):
      return [12, 6]
    case taxonomies.includes(TAXONOMIES_MAP.container_module.grid_4_columns):
      return [12, 4]
    case taxonomies.includes(TAXONOMIES_MAP.container_module.grid_3_columns):
      return [12, 6, 3]
    default:
      return
  }
}

const FlexCell: React.FC<FlexCellProps> = ({
  col,
  offset,
  sx,
  taxonomies,
  ...props
}) => {
  let taxonomiesColumnSize: number[] | undefined

  if (taxonomies) taxonomiesColumnSize = getTaxonomiesColumnSize(taxonomies)

  const colWidth = (
    taxonomiesColumnSize
      ? ColToWidth(taxonomiesColumnSize)
      : !!col
      ? ColToWidth(col)
      : null
  ) as ResponsiveStyleValue<string>

  const offsetWidth = (
    !!offset ? ColToWidth(offset) : null
  ) as ResponsiveStyleValue<string>

  return (
    <Box
      sx={{
        ...(colWidth && { width: colWidth }),
        ...(!colWidth && { flex: 1 }),
        ...(offsetWidth && { marginLeft: offsetWidth }),
        ...sx,
      }}
      {...props}
    />
  )
}

export { FlexRow, FlexCell }
